/**
 * Facet Button Component
 *
 * Filter pill buttons with multiple variants for the results page.
 * Figma: Facet Pill (Node: 51:7124)
 *
 * Variants:
 * - Active: Black background, white text, with X close button
 * - Inactive: White background with border, black text
 * - Inactive w/ Info: Like Inactive but with info icon
 * - Add-Suggestion: No border, just "Add Filter" with plus icon
 * - Inactive Summary: Like Add-Suggestion but gray with count (e.g., "2 more")
 *
 * Dimensions:
 * - Active/Inactive: height 24.49px
 * - Add-Suggestion/Inactive Summary: height 25.84px
 */
"use client";

import React from "react";

import Image from "next/image";

import { createPortal } from "react-dom";

import { buildCanonicalBrandPath } from "@/lib/brand-routing";
import { colors } from "@/styles/design-tokens";

export type FacetButtonVariant =
  | "active"
  | "inactive"
  | "inactive-info"
  | "add"
  | "summary"
  | "loading";

interface FacetButtonProps {
  variant?: FacetButtonVariant;
  label: string;
  onRemove?: () => void;
  onInfoClick?: () => void;
  onClick?: () => void;
  className?: string;
  justification?: string; // AI-generated justification for tooltips
  brandName?: string; // For generating brand page links
}

export function FacetButton({
  variant = "active",
  label,
  onRemove,
  onInfoClick,
  onClick,
  className = "",
  justification,
  brandName,
}: FacetButtonProps) {
  // Hooks must be called at the top level (Rules of Hooks)
  const [showTooltip, setShowTooltip] = React.useState(false);
  const [tooltipPosition, setTooltipPosition] = React.useState({
    top: 0,
    left: 0,
  });
  const buttonRef = React.useRef<HTMLElement>(null);

  // Update tooltip position when it shows
  React.useEffect(() => {
    if (showTooltip && buttonRef.current && (variant === "inactive-info" || variant === "active")) {
      const rect = buttonRef.current.getBoundingClientRect();
      setTooltipPosition({
        top: rect.top, // Position at top of button, transform handles offset
        left: rect.left,
      });
    }
  }, [showTooltip, variant]);

  const tooltipContent =
    showTooltip &&
    justification &&
    typeof window !== "undefined" &&
    createPortal(
      <div
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        style={{
          position: "fixed",
          top: `${tooltipPosition.top}px`,
          left: `${tooltipPosition.left}px`,
          transform: "translateY(calc(-100% - 2px))",
          zIndex: 9999,
          minWidth: "200px",
          maxWidth: "320px",
          whiteSpace: "normal",
          backgroundColor: "rgb(0, 0, 0)",
          color: "rgb(255, 255, 255)",
          padding: "10px 14px",
          borderRadius: "6px",
          fontSize: "12px",
          lineHeight: "1.5",
          fontWeight: "400",
          boxShadow: "0 10px 25px rgba(0, 0, 0, 0.5)",
          pointerEvents: brandName ? "auto" : "none",
        }}
      >
        {justification}
        {brandName && (
          <div style={{ marginTop: "8px" }}>
            <a
              href={buildCanonicalBrandPath(brandName)}
              style={{
                color: "rgb(255, 255, 255)",
                textDecoration: "underline",
                fontSize: "11px",
                fontWeight: "500",
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              View {brandName} collection →
            </a>
          </div>
        )}
        <div
          style={{
            position: "absolute",
            bottom: "-4px",
            left: "20px",
            width: 0,
            height: 0,
            borderLeft: "4px solid transparent",
            borderRight: "4px solid transparent",
            borderTop: "4px solid rgb(0, 0, 0)",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: "-6px",
            left: 0,
            right: 0,
            height: "10px",
            background: "transparent",
            pointerEvents: "auto",
          }}
        />
      </div>,
      document.body
    );

  // Active variant: Black background, white text, X button
  if (variant === "active") {
    return (
      <>
        <div
          ref={buttonRef as React.RefObject<HTMLDivElement>}
          data-testid="facet-chip"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          className={`inline-flex flex-shrink-0 items-center justify-center ${className}`}
          style={{
            backgroundColor: colors.stroke.pureBlack, // #000000
            height: "24.49px",
            borderRadius: "22622000px", // Fully rounded
            paddingLeft: "10px",
            paddingRight: "10px",
            paddingTop: "3px",
            paddingBottom: "3px",
            gap: "7px",
          }}
        >
          <span className="text-action whitespace-nowrap text-white">{label}</span>

          {/* Close Button - 13.989×13.989px */}
          <button
            onClick={onRemove}
            className="flex items-center justify-center"
            style={{
              width: "13.989px",
              height: "13.989px",
              borderRadius: "3.5px",
              padding: "1.749px",
            }}
            aria-label={`Remove ${label}`}
          >
            <Image src="/icons/x-icon-white.svg" alt="" width={10.492} height={10.492} />
          </button>
        </div>

        {tooltipContent}
      </>
    );
  }

  // Inactive variant: White background with border
  if (variant === "inactive") {
    return (
      <button
        data-testid="facet-chip"
        onClick={onClick}
        className={`inline-flex flex-shrink-0 items-center justify-center transition-opacity hover:opacity-80 ${className}`}
        style={{
          backgroundColor: colors.background.white,
          height: "24.49px",
          borderRadius: "22622000px",
          border: `0.674px solid ${colors.divider.default}`, // #D1D5DC
          paddingLeft: "11px",
          paddingRight: "11px",
          paddingTop: "3px",
          paddingBottom: "3px",
          cursor: onClick ? "pointer" : "default",
        }}
      >
        <span className="text-action text-ink whitespace-nowrap">{label}</span>
      </button>
    );
  }

  // Loading state variant: Empty gray pill with shimmer animation
  if (variant === "loading") {
    return (
      <div
        className={`facet-shimmer inline-flex items-center justify-center ${className}`}
        style={{
          height: "24px",
          width: "80px", // Exact Figma width
          borderRadius: "22622000px",
          paddingLeft: "10px",
          paddingRight: "10px",
          paddingTop: "3px",
          paddingBottom: "3px",
          gap: "7px",
        }}
      >
        {/* Empty - loading state shows just the pill shape */}
      </div>
    );
  }

  // Inactive with info icon
  if (variant === "inactive-info") {
    return (
      <>
        <button
          ref={buttonRef as React.RefObject<HTMLButtonElement>}
          data-testid="facet-chip"
          onClick={onClick}
          onMouseEnter={() => setShowTooltip(true)} // Hover on entire button
          onMouseLeave={() => setShowTooltip(false)}
          className={`inline-flex flex-shrink-0 items-center justify-center transition-opacity hover:opacity-80 ${className}`}
          style={{
            backgroundColor: colors.background.white,
            height: "24.49px",
            borderRadius: "22622000px",
            border: `0.674px solid ${colors.divider.default}`,
            paddingLeft: "11px",
            paddingRight: "11px",
            paddingTop: "3px",
            paddingBottom: "3px",
            gap: "7px",
            cursor: onClick || onInfoClick ? "pointer" : "default",
          }}
        >
          <span className="text-action text-ink whitespace-nowrap">{label}</span>

          {/* Info Icon - 14×14px (visual only, no hover handlers) */}
          <span
            onClick={(e) => {
              e.stopPropagation();
              onInfoClick?.();
            }}
            className="relative flex items-center justify-center"
            style={{
              width: "14px",
              height: "14px",
            }}
            aria-label={`Info about ${label}`}
          >
            <Image src="/icons/info.svg" alt="" width={14} height={14} />
          </span>
        </button>

        {tooltipContent}
      </>
    );
  }

  // Add Filter button: No border, black text with plus icon
  if (variant === "add") {
    return (
      <button
        onClick={onClick}
        className={`inline-flex flex-shrink-0 items-center justify-center transition-opacity hover:opacity-80 ${className}`}
        style={{
          backgroundColor: colors.accent.lightestGray, // #F1F1F3
          height: "25.84px",
          borderRadius: "22622000px",
          paddingLeft: "10px",
          paddingRight: "10px",
          paddingTop: "3px",
          paddingBottom: "3px",
          gap: "6px",
          cursor: onClick ? "pointer" : "default",
        }}
      >
        {/* Plus Icon - 10.492×10.492px */}
        <Image src="/icons/plus.svg" alt="" width={10.492} height={10.492} />

        <span className="text-action text-ink whitespace-nowrap">{label}</span>
      </button>
    );
  }

  // Summary variant: Gray background with count (e.g., "2 more")
  if (variant === "summary") {
    return (
      <div
        className={`inline-flex flex-shrink-0 items-center justify-center ${className}`}
        style={{
          backgroundColor: colors.accent.lightestGray, // #F1F1F3
          height: "25.84px",
          borderRadius: "22622000px",
          paddingLeft: "10px",
          paddingRight: "10px",
          paddingTop: "3px",
          paddingBottom: "3px",
          gap: "6px",
        }}
      >
        {/* Plus Icon in gray - 10.492×10.492px */}
        <Image
          src="/icons/plus.svg"
          alt=""
          width={10.492}
          height={10.492}
          style={{ opacity: 0.7 }} // Gray variant
        />

        <span className="text-action whitespace-nowrap text-ink-muted">{label}</span>
      </div>
    );
  }

  return null;
}
